import { BadgeCheck, MapPin, MessageSquare, Waves } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { IMAGES, SITE, CPO } from "@/content/site";

const VALUES = [
  {
    icon: BadgeCheck,
    title: "CPO certified professionalism",
    text: "Certified Pool Operator training means your water chemistry and equipment are managed to recognized industry standards — not guesswork.",
  },
  {
    icon: Waves,
    title: "Pools are all we do",
    text: "We're not a lawn company or a handyman service. Residential pool care is our entire focus, and it shows in the details.",
  },
  {
    icon: MessageSquare,
    title: "Communication is the product",
    text: "A clean pool is the baseline. Knowing what was done, what your water needs, and when something needs attention is what makes service feel professional.",
  },
  {
    icon: MapPin,
    title: "Pinellas County, period",
    text: "We work exclusively in Pinellas County. Our routes stay tight, our service days stay consistent, and our reputation stays local.",
  },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Point of Pines Pools & Service | Local Pinellas County Pool Company"
        description="Point of Pines Pools & Service is a locally owned, CPO certified residential pool company serving all of Pinellas County, FL — Clearwater, St. Petersburg, Largo, the beaches and beyond."
        path="/about/"
      />
      <PageHero
        overline="About Us"
        title="A Local Pool Company,"
        accent="Not a Franchise"
        description="Point of Pines Pools & Service was built on a simple idea: homeowners deserve pool service that shows up, does the work right, and communicates clearly. That's what we do — every week, across Pinellas County."
        image={IMAGES.communityPool}
        imageAlt="Condominium community pool and spa maintained by Point of Pines Pools & Service in Pinellas County FL"
      />

      <section data-testid="about-story-section" className="py-16 sm:py-24 lg:py-32 bg-shell">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">Our Story</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              Built one backyard at a time
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Point of Pines Pools &amp; Service is locally owned and operated right here in
              Pinellas County. We specialize in residential pool maintenance, water chemistry,
              equipment service and repairs, and new pool surface startups — the work that keeps
              Florida backyards swim-ready year-round.
            </p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Because we&apos;re local, our name is on every visit. We don&apos;t hide behind a
              national brand or a ticket system. When something matters to you about your pool,
              it matters to us — and you can reach the person responsible.
            </p>
            <div
              data-testid="about-cpo-credential"
              className="mt-8 rounded-2xl bg-navy text-white p-6 sm:p-7"
            >
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-sky2 font-semibold">
                <BadgeCheck className="w-4 h-4" /> Credentials on the record
              </div>
              <p className="mt-4 font-display font-bold text-lg tracking-tight">
                {CPO.holder} — {CPO.credential}
              </p>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                Issued by the {CPO.issuer} · Certification #{CPO.certNumber} · Certified{" "}
                {CPO.certified} · Valid through {CPO.expires}
              </p>
              <a
                href={CPO.image}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-cpo-certificate-link"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-aqua hover:text-sky2 transition-colors"
              >
                View the certificate
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="clip-arch overflow-hidden ring-1 ring-slate-200 shadow-xl shadow-navy/10">
              <img
                src={IMAGES.mikePole}
                alt="Mike Strawn of Point of Pines Pools & Service caring for a backyard pool surrounded by tropical Florida landscaping"
                className="w-full h-96 sm:h-[30rem] object-cover object-[50%_75%]"
                loading="lazy"
                data-testid="about-story-photo"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-owner-section" className="py-16 sm:py-24 lg:py-32 bg-navy text-white relative overflow-hidden">
        <div className="noise-overlay absolute inset-0 opacity-[0.04] pointer-events-none" />
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <Reveal>
              <div className="clip-arch overflow-hidden ring-1 ring-white/15 shadow-2xl shadow-black/40">
                <img
                  src={IMAGES.ownerMike}
                  alt="Mike Strawn, owner of Point of Pines Pools & Service, testing pool water chemistry at a Pinellas County pool"
                  className="w-full h-[26rem] sm:h-[32rem] object-cover object-top"
                  loading="lazy"
                  data-testid="about-owner-photo"
                />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="absolute -bottom-6 left-4 sm:left-8 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl shadow-navy/40 p-4 sm:p-5" data-testid="about-owner-badge">
                <p className="font-display font-bold text-navy text-sm">Mike Strawn</p>
                <p className="text-xs text-slate-500 mt-0.5">Owner &amp; Operator · CPO Certified</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:pl-6">
            <Reveal>
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-sky2 font-semibold">Meet the Owner</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15]">
                The kind of company I wanted to hire myself
              </h2>
              <p className="mt-6 text-base text-slate-300 leading-relaxed">
                I started Point of Pines Pools &amp; Service after experiencing firsthand how
                frustrating it can be to find a pool company you can truly rely on. As a pool owner
                myself, I went through everything from national companies to one-man operations, and
                the experience was often the same — different people showing up, inconsistent
                service, and promises that weren&apos;t always kept.
              </p>
              <p className="mt-4 text-base text-slate-300 leading-relaxed">
                I knew Pinellas County deserved better.
              </p>
              <p className="mt-4 text-base text-slate-300 leading-relaxed">
                I didn&apos;t start Point of Pines with the goal of becoming the biggest pool company
                in town. I started it to build the kind of company I wanted to hire myself: local,
                dependable, trustworthy, and committed to doing what we say we&apos;re going to do.
              </p>
              <p className="mt-4 text-base text-slate-300 leading-relaxed">
                I&apos;m a native Floridian, a Pinellas County resident, a husband, and soon-to-be
                dad. This is the community my family and I call home, and I take a lot of pride in
                serving the people who live here.
              </p>
              <blockquote
                data-testid="about-owner-quote"
                className="mt-8 border-l-4 border-aqua pl-6 py-1"
              >
                <p className="font-display text-lg sm:text-xl font-semibold tracking-tight leading-snug text-white">
                  &ldquo;For me, success isn&apos;t about having the most pools on a route. It&apos;s
                  about being the pool professional my customers know they can count on.&rdquo;
                </p>
                <cite className="mt-3 block text-sm font-mono uppercase tracking-[0.18em] text-sky2 not-italic">
                  — Mike Strawn, Owner
                </cite>
              </blockquote>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-values-section" className="py-16 sm:py-24 bg-mist">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">How We Work</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
              What you can expect from us
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 2) * 0.08}>
                <div data-testid={`about-value-${i}`} className="bg-white rounded-2xl border border-slate-200/80 p-7 sm:p-8 h-full transition-shadow duration-300 hover:shadow-xl hover:shadow-cyan-500/10">
                  <span className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center">
                    <v.icon className="w-6 h-6 text-sky2" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-semibold text-navy tracking-tight">{v.title}</h3>
                  <p className="mt-3 text-sm sm:text-[15px] text-slate-500 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="Want to work with a local pool pro?"
        text={`Call ${SITE.phone} or request a quote online — we'll take it from there.`}
      />
    </>
  );
}
